import React, { useCallback, useEffect, useState } from "react";
import { FaShieldAlt, FaPlus, FaCreditCard } from "react-icons/fa";
import api from "../utils/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { useModal } from "../context/ModalContext";
import { Badge, Button, Spinner } from "./ui";

export default function ManagedProxyAddonCard({ compact = false, onChanged }) {
  const { teamRole } = useAuth();
  const modal = useModal();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const isTeamMember = teamRole === "member";

  const load = useCallback(async () => {
    try {
      const response = await api.get("/api/managed-proxy-addon");
      setData(response?.data?.data || null);
    } catch (err) {
      console.error("managed proxy add-on load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("managed_proxy") !== "success") return undefined;
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      await load();
      if (attempts >= 6) window.clearInterval(timer);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [load]);

  const refresh = async () => {
    await load();
    onChanged?.();
  };

  const checkout = async () => {
    setAction("checkout");
    try {
      const response = await api.post("/api/managed-proxy-addon/checkout", { quantity: 1 });
      if (response?.data?.url) window.location.href = response.data.url;
      else modal.alert("Could not open managed proxy checkout.", "warning");
    } catch (err) {
      modal.alert(err?.response?.data?.message || "Could not start managed proxy checkout", "error");
    } finally {
      setAction(null);
    }
  };

  const addSeat = async () => {
    const current = data?.subscription?.seats || 0;
    const ok = await modal.confirm(
      `Add one managed proxy seat for $5/month? Stripe will prorate the current billing period.`,
      { confirmText: "Add seat" }
    );
    if (!ok) return;
    setAction("seat");
    try {
      const response = await api.post("/api/managed-proxy-addon/seats", { quantity: current + 1 });
      setData(response?.data?.data || data);
      modal.alert("Managed proxy seat added.", "success");
      await refresh();
    } catch (err) {
      modal.alert(err?.response?.data?.message || "Could not add a managed proxy seat", "error");
    } finally {
      setAction(null);
    }
  };

  const cancel = async () => {
    const ok = await modal.confirm(
      "Cancel Managed Proxy at the end of this billing period? Protected linked-device sessions will be stopped when access ends; they will never reconnect through Walytic's direct server IP.",
      { danger: true, confirmText: "Cancel add-on" }
    );
    if (!ok) return;
    setAction("cancel");
    try {
      const response = await api.post("/api/managed-proxy-addon/cancel");
      setData(response?.data?.data || data);
      modal.alert("Managed Proxy will cancel at the end of the billing period.", "success");
      await refresh();
    } catch (err) {
      modal.alert(err?.response?.data?.message || "Could not cancel Managed Proxy", "error");
    } finally {
      setAction(null);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-ink-muted"><Spinner size="sm" /> Loading Managed Proxy…</div>;
  if (!data) return null;

  const subscription = data.subscription;
  const active = subscription?.status === "active";
  const renewal = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
    : null;

  return (
    <div className={`rounded-xl border border-surface-border bg-surface-card ${compact ? "p-4" : "p-5"}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="rounded-lg bg-brand/10 p-2.5 text-brand"><FaShieldAlt /></div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-ink-primary">Walytic Managed Proxy</h3>
              <Badge variant={active ? "success" : subscription ? "warning" : "brand"}>
                {active ? "Active" : subscription?.status || "$5/month"}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-ink-muted">
              One private sticky proxy seat protects one WhatsApp linked-device number. Credentials stay on Walytic servers.
            </p>
          </div>
        </div>
        {!subscription && !isTeamMember && (
          <Button type="button" variant="primary" loading={action === "checkout"} disabled={!data.checkoutConfigured || !data.poolAvailable} onClick={checkout}>
            <FaCreditCard size={12} className="mr-2" /> Get for $5/month
          </Button>
        )}
      </div>

      {!data.poolAvailable && (
        <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Managed proxy capacity is temporarily unavailable. Bring your own proxy for now.</p>
      )}

      {subscription && (
        <div className="mt-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div><p className="text-xs text-ink-muted">Seats</p><p className="font-semibold text-ink-primary">{subscription.seats}</p></div>
            <div><p className="text-xs text-ink-muted">Assigned</p><p className="font-semibold text-ink-primary">{subscription.usedSeats}</p></div>
            <div><p className="text-xs text-ink-muted">Available</p><p className="font-semibold text-ink-primary">{subscription.availableSeats}</p></div>
            <div><p className="text-xs text-ink-muted">{subscription.cancelAtPeriodEnd ? "Access until" : "Renews"}</p><p className="font-semibold text-ink-primary">{renewal || "—"}</p></div>
          </div>

          {!!data.assignedSessions?.length && !compact && (
            <div className="mt-4 border-t border-surface-border pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Protected numbers</p>
              <div className="flex flex-wrap gap-2">
                {data.assignedSessions.map((session) => (
                  <span key={session.sessionId} className="rounded-full bg-surface-bg px-3 py-1 text-xs text-ink-secondary">
                    {session.number || session.sessionId}
                  </span>
                ))}
              </div>
            </div>
          )}

          {active && !subscription.cancelAtPeriodEnd && !isTeamMember && (
            <div className="mt-4 flex flex-wrap gap-2">
              <Button type="button" variant="secondary" loading={action === "seat"} onClick={addSeat}>
                <FaPlus size={11} className="mr-2" /> Add $5 seat
              </Button>
              {!compact && <Button type="button" variant="danger" loading={action === "cancel"} onClick={cancel}>Cancel add-on</Button>}
            </div>
          )}
        </div>
      )}

      {isTeamMember && !subscription && (
        <p className="mt-3 text-xs text-ink-muted">Ask a workspace admin to purchase a managed proxy seat.</p>
      )}
    </div>
  );
}
